import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { Trophy, Star, MapPin, Award, ArrowLeft } from "lucide-react";

const MEDAL = ["🥇", "🥈", "🥉"];

function StarRow({ stars, size = "sm" }) {
  const s = size === "lg" ? "w-4 h-4" : "w-3 h-3";
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${s} ${i <= stars ? "fill-amber-400 text-amber-500" : "text-slate-300"}`}/>
      ))}
    </div>
  );
}

const MONTH_HI = ["जनवरी","फ़रवरी","मार्च","अप्रैल","मई","जून","जुलाई","अगस्त","सितम्बर","अक्टूबर","नवम्बर","दिसम्बर"];

export default function ReporterLeaderboardPage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reporters/leaderboard").then((r) => setData(r.data)).catch(() => {}); }, []);
  const [yr, mo] = useMemo(() => (data?.month || "2026-01").split("-").map(Number), [data]);

  return (
    <>
      <PublicHeader/>
      {/* Hero with JAKIR MOMIN gold signature */}
      <section className="bg-gradient-to-br from-[#0F172A] via-[#111827] to-[#1E3A8A] text-white relative overflow-hidden">
        <div className="absolute -top-8 -right-6 opacity-5 text-[220px] font-devnagri-heading font-black leading-none pointer-events-none select-none text-amber-300">21</div>
        <div className="max-w-6xl mx-auto px-4 py-14 relative">
          <div className="flex items-center gap-3 mb-3">
            <Trophy className="w-6 h-6 text-amber-400"/>
            <span className="uppercase tracking-[0.28em] text-[11px] text-amber-300 font-black">Field Reporter Leaderboard</span>
          </div>
          <h1 className="font-devnagri-heading text-4xl md:text-6xl font-black leading-tight">हमारे <span className="text-amber-300">ज़मीनी</span> पत्रकार</h1>
          <p className="text-slate-300 text-base md:text-lg mt-3 max-w-3xl leading-relaxed">
            हर ख़बर के पीछे एक असली चेहरा — यह हैं वो फ़ील्ड रिपोर्टर जो छत्तीसगढ़ के गाँव-गाँव जाकर सच लाते हैं।
            उनकी मेहनत को हम पहचानते हैं, हर महीने प्रदर्शन के आधार पर।
          </p>
          <div className="mt-6 inline-flex items-center gap-4 bg-white/5 border border-amber-300/30 rounded-lg px-5 py-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">प्रधान संपादक की मुहर</div>
              <div className="font-serif italic text-amber-300 text-2xl md:text-3xl font-bold mt-0.5 tracking-wide" style={{ fontFamily: "'Playfair Display', serif" }}>JAKIR MOMIN</div>
              <div className="text-slate-400 text-[10px]">Chief Editor · INDIAN-NEWS-21</div>
            </div>
            {data?.month && <div className="border-l border-white/20 pl-4 text-sm text-slate-300"><b className="text-amber-300">{MONTH_HI[mo - 1]} {yr}</b> रैंकिंग</div>}
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 py-10" data-testid="reporter-leaderboard-page">
        {!data ? (
          <div className="p-10 text-center text-slate-500">लोड हो रहा है…</div>
        ) : data.reporters.length === 0 ? (
          <div className="p-16 text-center text-slate-500 italic bg-white border border-dashed border-slate-200 rounded-lg">
            इस महीने अभी तक कोई रिपोर्टर रैंकिंग नहीं
          </div>
        ) : (
          <>
            {/* Podium */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
              {data.reporters.slice(0, 3).map((r, i) => (
                <div key={r.staff_id} className={`rounded-2xl p-6 border-2 shadow-lg relative overflow-hidden ${
                  i === 0 ? "bg-gradient-to-br from-amber-50 to-yellow-100 border-amber-400 md:transform md:-translate-y-4" :
                  i === 1 ? "bg-gradient-to-br from-slate-50 to-slate-100 border-slate-400" :
                  "bg-gradient-to-br from-orange-50 to-orange-100 border-orange-400"
                }`} data-testid={`public-podium-${r.staff_id}`}>
                  <div className="absolute -top-2 right-3 text-6xl opacity-90 select-none">{MEDAL[i]}</div>
                  <div className="uppercase tracking-widest text-[10px] font-black" style={{ color: i === 0 ? "#B45309" : i === 1 ? "#475569" : "#C2410C" }}>
                    {i === 0 ? "इस महीने का सितारा" : i === 1 ? "दूसरा स्थान" : "तीसरा स्थान"}
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    {r.photo_url ? (
                      <img src={r.photo_url} alt={r.name} className="w-16 h-16 rounded-full object-cover border-3 border-white shadow-md"/>
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-white shadow-md flex items-center justify-center text-3xl font-black text-slate-700">{r.name[0]}</div>
                    )}
                    <div className="min-w-0">
                      <div className="font-devnagri-heading font-black text-[#0F172A] text-xl leading-tight truncate">{r.name}</div>
                      <div className="text-xs text-slate-600 mt-0.5">{r.designation}</div>
                      <div className="mt-1.5"><StarRow stars={r.stars} size="lg"/></div>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-1 text-center border-t border-white/50 pt-3">
                    <div><div className="text-2xl font-black text-emerald-700">{r.approved}</div><div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">प्रकाशित</div></div>
                    <div><div className="text-2xl font-black text-amber-700">{r.acceptance}%</div><div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Accept</div></div>
                    <div><div className="text-2xl font-black text-blue-700">{r.districts_covered}</div><div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">ज़िले</div></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Remaining reporters as cards */}
            {data.reporters.length > 3 && (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-6 bg-[#EA580C]"></div>
                  <h2 className="font-devnagri-heading font-black text-xl text-[#0F172A]">बाकी टीम</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.reporters.slice(3).map((r) => (
                    <div key={r.staff_id} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition flex items-center gap-3" data-testid={`public-row-${r.staff_id}`}>
                      <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 font-black flex items-center justify-center text-sm">#{r.rank}</div>
                      {r.photo_url ? (
                        <img src={r.photo_url} alt="" className="w-11 h-11 rounded-full object-cover shrink-0"/>
                      ) : (
                        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center font-black shrink-0">{r.name[0]}</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-[#0F172A] truncate">{r.name}</div>
                        <div className="text-[10px] text-slate-500 mb-0.5">{r.designation}</div>
                        <StarRow stars={r.stars}/>
                      </div>
                      <div className="text-right">
                        <div className="font-black text-emerald-700 text-lg">{r.approved}</div>
                        <div className="text-[9px] text-slate-500 uppercase tracking-widest">प्रकाशित</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Trust footer */}
            <div className="mt-10 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-6 flex items-start gap-4">
              <Award className="w-7 h-7 text-amber-600 shrink-0"/>
              <div>
                <div className="font-devnagri-heading font-black text-[#0F172A] text-lg">पारदर्शी पत्रकारिता</div>
                <p className="text-sm text-slate-700 leading-relaxed mt-1">
                  हर रिपोर्टर की रैंकिंग मासिक <b>प्रकाशित समाचारों की संख्या</b> और <b>editorial acceptance rate</b> पर आधारित है — पारदर्शी, वस्तुनिष्ठ और JAKIR MOMIN द्वारा प्रमाणित।
                </p>
                <div className="mt-3 font-serif italic text-amber-800 text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>— JAKIR MOMIN</div>
                <div className="text-[11px] text-amber-700">Chief Editor · भारत की आवाज़</div>
              </div>
            </div>
          </>
        )}
        <Link to="/" className="mt-8 inline-flex items-center gap-1.5 text-slate-500 hover:text-[#0F172A] text-sm"><ArrowLeft className="w-4 h-4"/>मुखपृष्ठ</Link>
      </main>
      <PublicFooter/>
    </>
  );
}
